import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CompanyRoles } from '../../_models/company.model';

@Injectable({
  providedIn: 'root'
})
export class DataPersistenceService {

  monitor_message_cookie_name: string = 'monitor_message_show';
  private readonly XOR_KEY: number = 46;

  private accessTokenSubject: BehaviorSubject<string | null>;
  public currentAccessToken$: Observable<string | null>;

  constructor(private cookieService: CookieService) {
    const storedToken = this.cookieService.get('accessToken') || null;
    this.accessTokenSubject = new BehaviorSubject<string | null>(storedToken);
    this.currentAccessToken$ = this.accessTokenSubject.asObservable();
  }

  getAccessToken(): string | null {
    return this.accessTokenSubject.value;
  }

  saveAccessToken(accessToken: string) {
    this.accessTokenSubject.next(accessToken);
    this.cookieService.set(
      'accessToken',
      accessToken,
      0,
      '/',
      environment.cookieDomain,
      true,
      'Lax'
    );
  }

  deleteAccessToken() {
    this.accessTokenSubject.next(null);
    this.cookieService.delete('accessToken');
  }

  getRefreshToken(): string {
    return this.cookieService.get('refreshToken') || '';
  }

  getCompanyGuidCookie(): string | '' {
    return this.cookieService.get('companyId') || '';
  }

  getCompanyIdentificationNumberCookie(): string | '' {
    return this.cookieService.get('identificationNumber') || '';
  }

  getCompanyIdCookieIdEmpresa(): string | '' {
    const idempresa = this.cookieService.get('IDEMPRESA') || '';
    if (!idempresa) {
      return '';
    }
    return this.decodeBase64WithXOR(idempresa);
  }

  getCompanyIdCookie(): string | '' {
    const payload = this.parseJWTToken(this.getAccessToken() || '');
    if (!payload || !payload.username) {
      return '';
    }
    const match = payload.username.match(/_(\d+)$/);
    return match ? match[1] : '';
  }

  getCompanyIdAndUsernameCookie(): string | '' {
    const payload = this.parseJWTToken(this.getAccessToken() || '');
    if (!payload || !payload.username) {
      return '';
    }
    return payload.username;
  }

  getUsername(): string {
    return this.getCompanyIdAndUsernameCookie().split('_')[0];
  }

  setIdentificationNumberCookie(identificationNumber: string) {
    this.cookieService.set(
      'identificationNumber',
      identificationNumber,
      0,
      '/',
      environment.cookieDomain,
      true,
      'Lax'
    );
  }

  setCompanyIdCookie(companyId: string) {
    this.cookieService.set(
      'companyId',
      companyId,
      0,
      '/',
      environment.cookieDomain,
      true,
      'Lax'
    );
  }

  setRoles(roles: CompanyRoles) {
    localStorage.setItem('roles', JSON.stringify(roles));
  }

  getRoles(): CompanyRoles {
    const roles = localStorage.getItem('roles');
    if (roles) {
      return JSON.parse(roles);
    } else {
      return {
        canModifySettings: false,
        canBlockOrUnblockSuppliers: false,
        canAddRankingRisk: false,
        canAddSupplierToMonitoring: false,
        canAddSubcontractor: false,
        canCrudContacts: false,
        canShowData: false,
        canShowDetailRisk: false,
        canExportExcelList: false,
        canGetInformationRisk: false,
        canAssignAndUnassignTags: false,
        canCrudTags: false
      };
    }
  }

  getMonitorMessage(): string {
    return localStorage.getItem(this.monitor_message_cookie_name) || '';
  }

  setMonitorMessage(message: string) {
    localStorage.setItem(this.monitor_message_cookie_name, message);
  }

  getCentroGestionCookie(): string {
    const idorgc = this.cookieService.get('IDORGC_W') || '';
    if (!idorgc) {
      return '';
    }
    return this.decodeBase64WithXOR(idorgc);
  }

  getSessionCookie(): string {
    return this.cookieService.get('session') || '';
  }

  getIdTokenData(): any {
    return this.parseJWTToken(this.cookieService.get('idToken') || '');
  }

  private parseJWTToken(token: string): any {
    if (!token) {
      return null;
    }

    try {
      const tokenParts = token.split('.');

      if (tokenParts.length !== 3) {
        return null;
      }

      const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = base64Payload.length % 4;
      const paddedBase64 = padding
        ? base64Payload + '='.repeat(4 - padding)
        : base64Payload;

      try {
        return JSON.parse(atob(paddedBase64));
      } catch (decodeError) {
        console.error('Error decoding JWT payload:', decodeError);
        return null;
      }
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  }

  private decodeBase64WithXOR(encodedStr: string): string {
    try {
      const base64Str = encodedStr
        .replace(/\|/g, '+')
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .replace(/[^A-Za-z0-9+/=]/g, '');

      if (!base64Str) {
        return '';
      }

      const padding = base64Str.length % 4;
      const paddedBase64 = padding
        ? base64Str + '='.repeat(4 - padding)
        : base64Str;

      try {
        const decodedBytes = Uint8Array.from(atob(paddedBase64), (c) =>
          c.charCodeAt(0)
        );
        const originalBytes = decodedBytes.map((byte) => byte ^ this.XOR_KEY);
        return new TextDecoder().decode(originalBytes);
      } catch (decodeError) {
        console.error('Error decoding base64:', decodeError);
        return '';
      }
    } catch (error) {
      console.error('Error processing encoded string:', error);
      return '';
    }
  }
}
